import clsx from "clsx";
import { Check, Eye, Info } from "lucide-react";

import { Button } from "./ui";
import { t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { readOnlyText, useDossierLock } from "../sharing/access";

/** Rappel d'enregistrement (R10, seul motif autorisé pour un dossier non enregistré) : les fiches d'objet et les
 *  vues qui ont besoin d'un dossier enregistré l'affichent. Le lien enregistre le dossier (Données initiales) ou,
 *  sans nom, désigne le champ à remplir.
 *
 *  - `inline` (défaut) : texte gris et lien sous le contenu d'une fiche ;
 *  - `banner` : bandeau pleine largeur sous un en-tête (`border-b bg-canvas px-4 py-3`), icône d'information.
 *
 *  `action` : clé française de ce que l'enregistrement permet (« déplacer une unité »), traduite ici. */
export function SaveDossierHint({
  action,
  className,
  variant = "inline",
}: {
  action: string;
  className?: string;
  variant?: "inline" | "banner";
}) {
  const exerciseId = useUi((s) => s.exerciseId);
  const requestSave = useUi((s) => s.requestSave);
  const dossier = useDossierLock();
  if (dossier.locked) {
    // Dossier en lecture seule pour le compte (docs/17 §3.2) : rien à enregistrer ; le texte du bandeau, et l'appui sur
    // le lien désigne le bandeau (action attendue : demander l'accès en modification).
    const owner = readOnlyText(dossier.ownerName);
    return variant === "banner" ? (
      <div className={clsx("flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line bg-canvas px-4 py-3", className)}>
        <span className="flex min-w-0 flex-1 items-start gap-2 text-ui leading-snug text-ink">
          <Eye size={14} className="mt-0.5 shrink-0 text-warn" />
          {owner}
        </span>
      </div>
    ) : (
      <div className={className}>
        <p className="text-meta leading-snug text-ink-muted">{owner}</p>
      </div>
    );
  }
  const text = exerciseId
    ? t("Pour {action}, enregistrez d'abord les modifications du dossier.", { action: t(action) })
    : t("Pour {action}, créez d'abord le dossier.", { action: t(action) });
  const link = (
    <Button variant="link" onClick={() => requestSave(true)}>
      <Check size={14} /> {exerciseId ? t("Enregistrer le dossier") : t("Créer le dossier")}
    </Button>
  );
  if (variant === "banner")
    return (
      <div className={clsx("flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line bg-canvas px-4 py-3", className)}>
        <span className="flex min-w-0 flex-1 items-start gap-2 text-ui leading-snug text-ink">
          <Info size={14} className="mt-0.5 shrink-0 text-ink-muted" />
          {text}
        </span>
        {link}
      </div>
    );
  return (
    <div className={className}>
      <p className="text-meta leading-snug text-ink-muted">{text}</p>
      <div className="mt-1">{link}</div>
    </div>
  );
}
